
import { useCallback, useEffect, useRef } from 'react';
import { useEntityRealtime, EntityRealtimeOptions } from '../core/useEntityRealtime';
import {
  SocketEvent,
  ThreadReactionAddedPayload,
  ThreadReactionRemovedPayload,
  CommentReactionAddedPayload,
  CommentReactionRemovedPayload,
  ExtendedSocket,
  ModuleTypeEnum
} from '@scrambl/shared';
import type { ThreadComment, Thread } from '@scrambl/shared';
import { useThreadStore } from '@/store/thread.store';
import { threadApi } from '@/lib/api/threads.api';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/query-keys';
import { useAuth } from '@/store/auth.store';
import { playNewMessageSound } from '@/lib/sounds';
import { createLogger } from '@/lib/logger';

const logger = createLogger('lib/realtime/features/useThreadRealtime');

// Debug mode flag - enable via localStorage
const isBrowser = typeof window !== 'undefined' && typeof localStorage !== 'undefined';
const DEBUG_MODE = import.meta.env.DEV &&
  isBrowser &&
  (localStorage.getItem('REALTIME_DEBUG') === 'true' || localStorage.getItem('DEBUG_ALL') === 'true');

// Create a module-level event tracker to avoid duplicate processing across hook instances
const processedEvents = new Map<string, number>();
const EVENT_TTL = 10000; // 10 seconds

// Track when threads were last loaded with fresh data (to skip unnecessary catch-up)
const threadLastFetchedAt = new Map<string, number>();
const SKIP_CATCHUP_WINDOW = 5000; // Skip catch-up if data loaded within 5 seconds

/**
 * Mark a thread as having just been loaded with fresh data.
 * This prevents the catch-up mechanism from making redundant API calls.
 */
export function markThreadDataFetched(threadId: string): void {
  threadLastFetchedAt.set(threadId, Date.now());
}

// Helper to update comment count in task cache
function updateTaskCommentCount(
  queryClient: QueryClient,
  moduleType: ModuleTypeEnum | string | undefined,
  moduleId: string | undefined,
  delta: number
) {
  // Check for TASK module type (handle both string and enum)
  const isTaskModule = moduleType === 'TASK' || moduleType === ModuleTypeEnum.TASK;
  if (!isTaskModule || !moduleId) {
    return;
  }

  // Update all task-related query caches
  queryClient.setQueriesData(
    { queryKey: ['tasks'], exact: false },
    (oldData: unknown) => {
      if (!oldData) return oldData;

      // Handle useTaskData format: { tasks: TaskWithAssignments[], meta: ... }
      if (typeof oldData === 'object' && 'tasks' in oldData && Array.isArray((oldData as { tasks: unknown[] }).tasks)) {
        const taskData = oldData as { tasks: Array<{ id: string; commentCount?: number }>; meta?: unknown };
        return {
          ...taskData,
          tasks: taskData.tasks.map((task) =>
            task.id === moduleId
              ? { ...task, commentCount: Math.max(0, (task.commentCount || 0) + delta) }
              : task
          ),
        };
      }

      // Handle paginated response format: { data: [...], meta: ... }
      if (typeof oldData === 'object' && 'data' in oldData && Array.isArray((oldData as { data: unknown[] }).data)) {
        const paginatedData = oldData as { data: Array<{ id: string; commentCount?: number }>; meta?: unknown };
        return {
          ...paginatedData,
          data: paginatedData.data.map((task) =>
            task.id === moduleId
              ? { ...task, commentCount: Math.max(0, (task.commentCount || 0) + delta) }
              : task
          ),
        };
      }

      // Handle array format
      if (Array.isArray(oldData)) {
        return (oldData as Array<{ id: string; commentCount?: number }>).map((task) =>
          task.id === moduleId
            ? { ...task, commentCount: Math.max(0, (task.commentCount || 0) + delta) }
            : task
        );
      }

      return oldData;
    }
  );
}

// Clean up old events and fetch timestamps periodically
if (typeof window !== 'undefined') {
  setInterval(() => {
    const now = Date.now();
    processedEvents.forEach((timestamp, eventId) => {
      if (now - timestamp > EVENT_TTL) {
        processedEvents.delete(eventId);
      }
    });
    // Clean up old fetch timestamps
    threadLastFetchedAt.forEach((timestamp, threadId) => {
      if (now - timestamp > 60000) { // Clean up after 1 minute
        threadLastFetchedAt.delete(threadId);
      }
    });
  }, 60000); // Check every minute
}

// Helper function for conditional logging
function debugLog(..._args: unknown[]) {
  // Debug logging disabled - enable REALTIME_DEBUG in localStorage
}

function getAuthorNameFromComment(comment: ThreadComment): string {
  const author = comment.author;
  if (!author) {
    return 'Unknown';
  }
  if (author.displayName?.trim()) {
    return author.displayName.trim();
  }
  const name = [author.firstName, author.lastName].filter(Boolean).join(' ').trim();
  if (name) {
    return name;
  }
  return author.email || 'Unknown';
}

function isThreadCommentQueryKey(
  queryKey: unknown,
  threadId: string,
  parentCommentId?: string | null
) {
  if (!Array.isArray(queryKey)) {
    return false;
  }
  if (queryKey.length < 4) {
    return false;
  }
  if (queryKey[0] !== 'threads' || queryKey[1] !== 'detail' || queryKey[2] !== threadId || queryKey[3] !== 'comments') {
    return false;
  }
  if (parentCommentId === undefined) {
    return true;
  }
  const keyParent = queryKey[4] ?? null;
  return keyParent === (parentCommentId ?? null);
}


export interface UseThreadRealtimeOptions extends EntityRealtimeOptions {
  onCommentCreated?: (comment: ThreadComment) => void;
  onCommentUpdated?: (comment: ThreadComment) => void;
  onCommentDeleted?: (data: { id: string, threadId: string }) => void;
  onThreadDeleted?: (data: { id: string }) => void;
  onReactionAdded?: (data: ThreadReactionAddedPayload) => void;
  onReactionRemoved?: (data: ThreadReactionRemovedPayload) => void;
  /** Module type for this thread (e.g., TASK) - used to update module-specific caches */
  moduleType?: string;
  /** Module ID for this thread - used to update module-specific caches */
  moduleId?: string;
}

/**
 * Hook for real-time thread updates
 * Uses the enhanced useEntityRealtime with standardized client-generated ID tracking
 */
export function useThreadRealtime(threadId: string | null | undefined, options: UseThreadRealtimeOptions = {}) {
  const { 
    addThreadComment,
    updateThreadComment,
    removeThreadCommentById,
    handleThreadReactionAdded,
    handleThreadReactionRemoved,
    handleCommentReactionAdded,
    handleCommentReactionRemoved,
    setThreadCommentLocalStatus,
    getPendingCommentsForThread,
    markPendingCommentAttempt,
    markPendingCommentFailed,
    removePendingComment
  } = useThreadStore();
  const queryClient = useQueryClient();

  const updateThreadDetailCache = useCallback((threadId: string, updater: (thread: Thread) => Thread) => {
    queryClient.setQueryData(queryKeys.threads.detail(threadId), (existing) => {
      if (!existing || typeof existing !== 'object') {
        return existing;
      }
      return updater(existing as Thread);
    });
  }, [queryClient]);

  const updateThreadListCaches = useCallback((threadId: string, updater: (thread: Thread) => Thread) => {
    queryClient.setQueriesData(
      { queryKey: queryKeys.threads.all, exact: false },
      (existing: unknown) => {
        if (!existing) return existing;

        const updateThreadArray = (list: Thread[]) =>
          list.map(thread => thread.id === threadId ? updater(thread) : thread);

        if (typeof existing === 'object' && existing !== null && 'pages' in existing) {
          const data = existing as {
            pages: Array<{ data?: Thread[] }>;
            pageParams: unknown[];
          };

          const pages = data.pages.map(page => {
            if (!page?.data) return page;
            if (!page.data.some(item => item.id === threadId)) return page;
            return {
              ...page,
              data: updateThreadArray(page.data),
            };
          });

          return { ...data, pages };
        }

        if (typeof existing === 'object' && existing !== null && 'data' in existing) {
          const data = existing as { data?: Thread[] };
          if (!data.data || !data.data.some(item => item.id === threadId)) {
            return existing;
          }
          return {
            ...data,
            data: updateThreadArray(data.data),
          };
        }

        if (typeof existing === 'object' && existing !== null && 'id' in existing) {
          const data = existing as Thread;
          return data.id === threadId ? updater(data) : existing;
        }

        return existing;
      },
    );
  }, [queryClient]);

  const upsertThreadCommentInQueries = useCallback((comment: ThreadComment, metaDelta?: number) => {
    queryClient.setQueriesData(
      {
        predicate: (query) => isThreadCommentQueryKey(query.queryKey, comment.threadId, comment.parentCommentId),
      },
      (existing) => {
        if (!existing) return existing;

        if (Array.isArray(existing)) {
          const list = existing as ThreadComment[];
          const existingIndex = list.findIndex(item => item.id === comment.id);
          if (existingIndex === -1) {
            return [comment, ...list];
          }
          const updated = [...list];
          updated[existingIndex] = { ...updated[existingIndex], ...comment };
          return updated;
        }

        if (typeof existing === 'object' && existing !== null && 'data' in existing) {
          const data = existing as { data?: ThreadComment[]; meta?: { totalItems?: number } };
          const list = data.data ?? [];
          const existingIndex = list.findIndex(item => item.id === comment.id);
          const didInsert = existingIndex === -1;
          const updatedData = didInsert
            ? [comment, ...list]
            : list.map(item => item.id === comment.id ? { ...item, ...comment } : item);

          let meta = data.meta;
          if (meta && typeof meta.totalItems === 'number' && typeof metaDelta === 'number' && metaDelta !== 0 && didInsert) {
            meta = { ...meta, totalItems: Math.max(0, meta.totalItems + metaDelta) };
          }

          return {
            ...data,
            data: updatedData,
            meta,
          };
        }

        return existing;
      },
    );
  }, [queryClient]);

  const updateThreadCommentByIdInQueries = useCallback((
    threadId: string,
    commentId: string,
    updater: (comment: ThreadComment) => ThreadComment,
    metaDelta?: number
  ) => {
    queryClient.setQueriesData(
      {
        predicate: (query) => isThreadCommentQueryKey(query.queryKey, threadId),
      },
      (existing) => {
        if (!existing) return existing;

        if (Array.isArray(existing)) {
          const list = existing as ThreadComment[];
          const index = list.findIndex(item => item.id === commentId);
          if (index === -1) {
            return list;
          }
          const updated = [...list];
          updated[index] = updater(updated[index]);
          return updated;
        }

        if (typeof existing === 'object' && existing !== null && 'data' in existing) {
          const data = existing as { data?: ThreadComment[]; meta?: { totalItems?: number } };
          const list = data.data ?? [];
          const index = list.findIndex(item => item.id === commentId);
          if (index === -1) {
            return existing;
          }
          const updatedData = [...list];
          updatedData[index] = updater(updatedData[index]);

          let meta = data.meta;
          if (meta && typeof meta.totalItems === 'number' && typeof metaDelta === 'number' && metaDelta !== 0) {
            meta = { ...meta, totalItems: Math.max(0, meta.totalItems + metaDelta) };
          }

          return {
            ...data,
            data: updatedData,
            meta,
          };
        }

        return existing;
      },
    );
  }, [queryClient]);
  
  // Track options to avoid stale closures
  const optionsRef = useRef(options);
  
  // For debugging - track active instances
  const instanceId = useRef(`thread-${Math.random().toString(36).substring(2, 8)}`);
  
  // Batch updates for improved performance
  const pendingUpdatesRef = useRef<{
    commentCreated: ThreadComment[],
    commentUpdated: ThreadComment[],
    commentDeleted: { id: string, threadId: string }[],
    reactionAdded: ThreadReactionAddedPayload[],
    reactionRemoved: ThreadReactionRemovedPayload[]
  }>({
    commentCreated: [],
    commentUpdated: [],
    commentDeleted: [],
    reactionAdded: [],
    reactionRemoved: []
  });
  
  // Update options ref when options change
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);
  
  // Map thread-specific callbacks to entity events
  const entityOptions: EntityRealtimeOptions = {
    ...options,
    // Basic entity lifecycle - original event handlers
    onEntityDeleted: options.onThreadDeleted,
  };
  
  // Use the entity hook for common functionality - now with enhanced client ID tracking
  const {
    isConnected,
    activeConnections,
    trackClientEntity,
    isClientGenerated,
    addEventListener,
    socket,
    markEventProcessed: markEntityEventProcessed,
    hasProcessedEvent: hasProcessedEntityEvent
  } = useEntityRealtime('thread', threadId, entityOptions);

  const lastCatchupRef = useRef<number>(0);
  const wasConnectedRef = useRef<boolean>(false);
  const lastServerTimeRef = useRef<number | null>(null);
  
  // Global event processed check that works across hook instances
  const hasProcessedEvent = useCallback((eventId: string): boolean => {
    return processedEvents.has(eventId) || hasProcessedEntityEvent(eventId);
  }, [hasProcessedEntityEvent]);
  
  // Global event processing tracking
  const markEventProcessed = useCallback((eventId: string): void => {
    processedEvents.set(eventId, Date.now());
    markEntityEventProcessed(eventId);
  }, [markEntityEventProcessed]);

  useEffect(() => {
    wasConnectedRef.current = false;
    lastServerTimeRef.current = null;
  }, [threadId]);

  useEffect(() => {
    if (!threadId) return;

    if (!isConnected) {
      wasConnectedRef.current = false;
      return;
    }

    if (wasConnectedRef.current) {
      return;
    }

    wasConnectedRef.current = true;

    const now = Date.now();
    if (now - lastCatchupRef.current < 3000) {
      return;
    }
    lastCatchupRef.current = now;

    const catchUp = async () => {
      // Skip catch-up if data was just loaded (prevents redundant API call after initial fetch)
      const lastFetchedAt = threadLastFetchedAt.get(threadId);
      if (lastFetchedAt && (Date.now() - lastFetchedAt) < SKIP_CATCHUP_WINDOW) {
        return;
      }

      const { threadComments } = useThreadStore.getState();
      const threadCommentsForThread = threadComments.filter(comment => comment.threadId === threadId);
      if (threadCommentsForThread.length === 0) {
        return;
      }

      let maxSequence = threadCommentsForThread.reduce((max, comment) => {
        return typeof comment.sequence === 'number' ? Math.max(max, comment.sequence) : max;
      }, 0);

      const lastUpdatedAtMs = threadCommentsForThread.reduce((max, comment) => {
        const updatedAtValue = comment.updatedAt || comment.editedAt || comment.deletedAt || comment.createdAt;
        const updatedAtMs = updatedAtValue ? new Date(updatedAtValue).getTime() : 0;
        return Math.max(max, updatedAtMs);
      }, 0);

      let sinceUpdatedAt = lastServerTimeRef.current
        ? new Date(lastServerTimeRef.current).toISOString()
        : (lastUpdatedAtMs ? new Date(lastUpdatedAtMs).toISOString() : undefined);

      if (maxSequence === 0 && !sinceUpdatedAt) {
        return;
      }

      for (let attempt = 0; attempt < 10; attempt += 1) {
        const response = await threadApi.getThreadCommentChanges({
          threadId,
          sinceSequence: maxSequence,
          sinceUpdatedAt,
          limit: 500,
        });

        const { data, meta } = response;
        const serverTimeMs = meta?.serverTime ? new Date(meta.serverTime).getTime() : null;
        if (serverTimeMs && Number.isFinite(serverTimeMs)) {
          lastServerTimeRef.current = serverTimeMs;
        }

        const currentState = useThreadStore.getState();
        const existingCommentIds = new Set(currentState.threadComments.map(comment => comment.id));
        let maxUpdatedAtInBatch = 0;

        if (Array.isArray(data)) {
          data.forEach((comment) => {
            if (!comment || comment.threadId !== threadId) return;

            const isDeleted = !!comment.deletedAt;
            const exists = existingCommentIds.has(comment.id);

            if (!exists && !isDeleted) {
              addThreadComment({
                id: comment.id,
                threadId: comment.threadId,
                ...comment,
              });
            } else {
              updateThreadComment(comment);
            }

            if (typeof comment.sequence === 'number') {
              maxSequence = Math.max(maxSequence, comment.sequence);
            }

            const updatedAtValue = comment.updatedAt || comment.editedAt || comment.deletedAt || comment.createdAt;
            const updatedAtMs = updatedAtValue ? new Date(updatedAtValue).getTime() : 0;
            maxUpdatedAtInBatch = Math.max(maxUpdatedAtInBatch, updatedAtMs);
          });
        }

        const hasMore = meta?.hasMore === true;
        if (!hasMore && typeof meta?.lastCommentSequence === 'number' && maxSequence >= meta.lastCommentSequence) {
          break;
        }

        if (hasMore && maxUpdatedAtInBatch > 0) {
          sinceUpdatedAt = new Date(maxUpdatedAtInBatch).toISOString();
        } else if (serverTimeMs && Number.isFinite(serverTimeMs)) {
          sinceUpdatedAt = new Date(serverTimeMs).toISOString();
        } else if (!data || data.length === 0) {
          break;
        }
      }
    };

    void catchUp().catch((error) => {
      logger.error(`[Thread:${threadId}] Failed to catch up comments after reconnect:`, error);
    });
  }, [addThreadComment, isConnected, threadId, updateThreadComment]);

  const retryInFlightRef = useRef(false);
  const lastRetryRef = useRef<number>(0);

  const hydratePendingComments = useCallback(() => {
    if (!threadId) return;

    const pending = getPendingCommentsForThread(threadId);
    if (pending.length === 0) return;

    const { threadComments } = useThreadStore.getState();
    const existingIds = new Set(
      threadComments.map(comment => comment.clientMessageId ?? comment.id)
    );

    let nextSequence = threadComments
      .filter(comment => comment.threadId === threadId)
      .reduce((max, comment) => typeof comment.sequence === 'number' ? Math.max(max, comment.sequence) : max, 0);

    pending.forEach((pendingComment) => {
      if (existingIds.has(pendingComment.clientMessageId)) {
        return;
      }

      nextSequence += 1;
      addThreadComment({
        id: pendingComment.clientMessageId,
        clientMessageId: pendingComment.clientMessageId,
        threadId: pendingComment.threadId,
        content: pendingComment.content,
        parentCommentId: pendingComment.parentCommentId ?? null,
        createdAt: new Date(pendingComment.createdAt),
        updatedAt: new Date(pendingComment.createdAt),
        editedAt: null,
        deletedAt: null,
        organizationId: pendingComment.organizationId,
        authorId: pendingComment.authorId,
        author: pendingComment.author,
        sequence: nextSequence,
        userReaction: null,
        totalReactions: 0,
        totalReplies: 0,
        attachmentCount: 0,
        hasAttachments: false,
        localStatus: pendingComment.status,
      });
    });
  }, [addThreadComment, getPendingCommentsForThread, threadId]);

  useEffect(() => {
    hydratePendingComments();
  }, [hydratePendingComments]);

  const applyCreatedComment = useCallback((comment: ThreadComment, notify: boolean) => {
    addThreadComment({
      id: comment.id,
      threadId: comment.threadId,
      ...comment
    });

    upsertThreadCommentInQueries(comment, 1);

    // Update task comment count if this is a task comment
    updateTaskCommentCount(queryClient, optionsRef.current.moduleType, optionsRef.current.moduleId, 1);

    const preview = {
      content: comment.content,
      authorName: getAuthorNameFromComment(comment),
      createdAt: new Date(comment.createdAt),
    };
    const updatedAt = new Date(comment.createdAt);
    const updateThread = (thread: Thread) => ({
      ...thread,
      totalComments: Math.max(0, (thread.totalComments ?? 0) + 1),
      lastCommentPreview: preview,
      updatedAt,
    });
    updateThreadDetailCache(comment.threadId, updateThread);
    updateThreadListCaches(comment.threadId, updateThread);

    if (comment.clientMessageId) {
      removePendingComment(comment.clientMessageId);
    }

    // Play sound for new messages from other users
    if (notify) {
      const currentUserId = useAuth.getState().user?.id;
      if (comment.authorId !== currentUserId) {
        playNewMessageSound();
      }

      if (optionsRef.current.onCommentCreated) {
        optionsRef.current.onCommentCreated(comment);
      }
    }
  }, [
    addThreadComment,
    removePendingComment,
    upsertThreadCommentInQueries,
    updateThreadDetailCache,
    updateThreadListCaches,
    queryClient,
  ]);

  const retryPendingComments = useCallback(async () => {
    if (!threadId) return;
    if (retryInFlightRef.current) return;

    const now = Date.now();
    if (now - lastRetryRef.current < 2000) {
      return;
    }

    const pending = getPendingCommentsForThread(threadId);
    if (pending.length === 0) return;

    retryInFlightRef.current = true;
    lastRetryRef.current = now;

    try {
      for (const pendingComment of pending) {
        const lastAttemptAt = pendingComment.lastAttemptAt
          ? new Date(pendingComment.lastAttemptAt).getTime()
          : 0;
        if (lastAttemptAt && now - lastAttemptAt < 2000) {
          continue;
        }

        markPendingCommentAttempt(pendingComment.clientMessageId);
        setThreadCommentLocalStatus(pendingComment.clientMessageId, 'pending');

        try {
          const result = await threadApi.createThreadComment({
            threadId: pendingComment.threadId,
            parentCommentId: pendingComment.parentCommentId ?? null,
            content: pendingComment.content,
            clientMessageId: pendingComment.clientMessageId,
            moduleType: pendingComment.moduleType,
            moduleId: pendingComment.moduleId,
          });

          trackClientEntity(result.id);
          applyCreatedComment(result, false);
        } catch (error) {
          markPendingCommentFailed(pendingComment.clientMessageId);
          setThreadCommentLocalStatus(pendingComment.clientMessageId, 'failed');
          logger.error(`[Thread:${threadId}] Failed to resend pending comment:`, error);
        }
      }
    } finally {
      retryInFlightRef.current = false;
    }
  }, [
    applyCreatedComment,
    getPendingCommentsForThread,
    markPendingCommentAttempt,
    markPendingCommentFailed,
    setThreadCommentLocalStatus,
    threadId,
    trackClientEntity,
  ]);

  useEffect(() => {
    if (!isConnected) return;
    if (!threadId) return;
    if (!wasConnectedRef.current) return;

    void retryPendingComments();
  }, [isConnected, retryPendingComments, threadId]);

  // Process new comment with duplicate detection and organization filtering
  const handleNewComment = useCallback((comment: ThreadComment) => {
    // Skip if comment doesn't belong to the current thread
    if (!threadId || comment.threadId !== threadId) {
      debugLog(`[Thread:${threadId}] Skipped comment ${comment.id} (wrong thread)`);
      return;
    }
    
    // Create a unique event ID for this comment
    const eventId = (comment as { _eventId?: string })._eventId ?? `comment-created-${comment.id}`;
    
    // Skip if already processed or is client-generated
    if (hasProcessedEvent(eventId) || isClientGenerated(comment.id)) {
      debugLog(`[Thread:${threadId}] Skipped comment ${comment.id} (already processed or client-generated)`);
      return;
    }
    
    // Mark as processed globally
    markEventProcessed(eventId);
    
    applyCreatedComment(comment, true);
  }, [threadId, hasProcessedEvent, markEventProcessed, isClientGenerated, applyCreatedComment]);

  // Process comment update
  const handleCommentUpdate = useCallback((comment: ThreadComment) => {
    // Skip if comment doesn't belong to the current thread
    if (!threadId || comment.threadId !== threadId) {
      debugLog(`[Thread:${threadId}] Skipped comment update ${comment.id} (wrong thread)`);
      return;
    }
    
    // Create a unique event ID for this update
    const eventId = (comment as { _eventId?: string })._eventId
      ?? `comment-updated-${comment.id}-${comment.editedAt || comment.updatedAt || comment.deletedAt || comment.createdAt || ''}`;
    
    // Skip if already processed
    if (hasProcessedEvent(eventId)) {
      debugLog(`[Thread:${threadId}] Skipped comment update ${comment.id} (already processed)`);
      return;
    }
    
    // Mark as processed globally
    markEventProcessed(eventId);
    
    // Update the comment in the store
    updateThreadComment(comment);
    upsertThreadCommentInQueries(comment);
    
    // Notify callback if provided
    if (optionsRef.current.onCommentUpdated) {
      optionsRef.current.onCommentUpdated(comment);
    }
  }, [threadId, hasProcessedEvent, markEventProcessed, updateThreadComment, upsertThreadCommentInQueries]);
  
  // Remove the batching interval since we're handling comments directly
  useEffect(() => {
    // Skip if no thread ID
    if (!threadId) return;
    
    const interval = setInterval(() => {
      const updates = pendingUpdatesRef.current;
      
      // Only process reactions in batches now
      if (updates.reactionAdded.length === 0 && updates.reactionRemoved.length === 0) {
        return;
      }
      
      // Process reactions
      updates.reactionAdded.forEach(reaction => {
        handleThreadReactionAdded(reaction);
        if (optionsRef.current.onReactionAdded) {
          optionsRef.current.onReactionAdded(reaction);
        }
      });
      
      // Process removed reactions
      updates.reactionRemoved.forEach(data => {
        handleThreadReactionRemoved(data);
        if (optionsRef.current.onReactionRemoved) {
          optionsRef.current.onReactionRemoved(data);
        }
      });
      
      // Clear pending updates
      pendingUpdatesRef.current = {
        commentCreated: [],
        commentUpdated: [],
        commentDeleted: [],
        reactionAdded: [],
        reactionRemoved: []
      };
    }, 300); // Process batches every 300ms
    
    return () => clearInterval(interval);
  }, [threadId, handleThreadReactionAdded, handleThreadReactionRemoved]);
  
  // Process deleted comment
  const handleDeletedComment = useCallback((data: { id: string, threadId: string }) => {
    // Skip if not for this thread
    if (!threadId || data.threadId !== threadId) {
      return;
    }
    
    // Create a unique event ID
    const eventId = (data as { _eventId?: string })._eventId ?? `comment-deleted-${data.id}`;
    
    // Skip if already processed
    if (hasProcessedEvent(eventId)) {
      return;
    }
    
    // Mark as processed globally
    markEventProcessed(eventId);
    
    const { threadComments } = useThreadStore.getState();
    const existingComment = threadComments.find(comment => comment.id === data.id);
    const shouldDecrement = !existingComment?.deletedAt;

    updateThreadCommentByIdInQueries(
      data.threadId,
      data.id,
      (comment) => ({
        ...comment,
        content: '',
        deletedAt: new Date(),
      }),
      shouldDecrement ? -1 : 0,
    );

    if (shouldDecrement) {
      const updateThread = (thread: Thread) => ({
        ...thread,
        totalComments: Math.max(0, (thread.totalComments ?? 0) - 1),
      });
      updateThreadDetailCache(data.threadId, updateThread);
      updateThreadListCaches(data.threadId, updateThread);
    }

    removeThreadCommentById(data.id);

    if (optionsRef.current.onCommentDeleted) {
      optionsRef.current.onCommentDeleted(data);
    }
  }, [threadId, hasProcessedEvent, markEventProcessed, removeThreadCommentById, updateThreadCommentByIdInQueries, updateThreadDetailCache, updateThreadListCaches]);
  
  // Handler for comment reaction added
  const handleNewCommentReaction = useCallback((reaction: CommentReactionAddedPayload) => {
    if (!threadId || reaction.threadId !== threadId) return;
    if (isClientGenerated(reaction.id)) return;
    
    // Process directly - typically low volume
    handleCommentReactionAdded(reaction); 
  }, [threadId, handleCommentReactionAdded, isClientGenerated]);

  // Handler for comment reaction removed
  const handleDeletedCommentReaction = useCallback((data: CommentReactionRemovedPayload) => {
    if (!threadId || data.threadId !== threadId) return;
    
    // Process directly - typically low volume
    handleCommentReactionRemoved(data);
  }, [threadId, handleCommentReactionRemoved]);
  
  // Add thread-specific event handlers
  useEffect(() => {
    if (!socket || !threadId) return;
    
    debugLog(`[Thread:${threadId}] Setting up event handlers`);
    
    // Add direct socket listener as a fallback
    const onNewComment = (data: Record<string, unknown>) => {
      // Type check and convert the data
      if (isThreadComment(data)) {
        handleNewComment(data);
      }
    };

    // Set up socket event listeners using addEventListener from useEntityRealtime
    const cleanupComment = addEventListener(SocketEvent.THREAD_COMMENT_CREATED, (data: Record<string, unknown>) => {
      if (isThreadComment(data)) {
        handleNewComment(data);
      }
    });

    const cleanupCommentUpdate = addEventListener(SocketEvent.THREAD_COMMENT_UPDATED, (data: Record<string, unknown>) => {
      if (isThreadComment(data)) {
        handleCommentUpdate(data);
      }
    });

    const cleanupCommentDel = addEventListener(SocketEvent.THREAD_COMMENT_DELETED, handleDeletedComment);
    const cleanupReactionAdd = addEventListener(SocketEvent.THREAD_REACTION_ADDED, handleNewCommentReaction);
    const cleanupReactionRemove = addEventListener(SocketEvent.THREAD_REACTION_REMOVED, handleDeletedCommentReaction);

    (socket as ExtendedSocket).on(SocketEvent.THREAD_COMMENT_CREATED, onNewComment);
    
    // Cleanup function
    return () => {
      // Only log cleanup with debug mode on
      if (DEBUG_MODE) {
        debugLog(`[Thread:${threadId}] Cleaning up event handlers`);
      }
      
      cleanupComment();
      cleanupCommentUpdate();
      cleanupCommentDel();
      cleanupReactionAdd();
      cleanupReactionRemove();
      
      // Clean up direct socket listener
      (socket as ExtendedSocket).off(SocketEvent.THREAD_COMMENT_CREATED, onNewComment);
    };
  }, [
    socket, 
    threadId, 
    addEventListener,
    handleNewComment,
    handleCommentUpdate, 
    handleDeletedComment,
    handleNewCommentReaction,
    handleDeletedCommentReaction
  ]);
  
  // Simplified client-generated comment tracking that uses the entity tracking
  const trackClientComment = useCallback((id: string) => {
    // Track client-generated comments to prevent duplication
    trackClientEntity(id);
  }, [trackClientEntity]);
  
  // Simplified client-generated reaction tracking that uses the entity tracking
  const trackClientReaction = useCallback((id: string) => {
    // Track client-generated reactions to prevent duplication
    trackClientEntity(id);
  }, [trackClientEntity]);
  
  return {
    isConnected,
    activeConnections,
    trackClientComment,
    trackClientReaction,
    isClientGenerated,
    debug: {
      enableDebug: () => {
        localStorage.setItem('REALTIME_DEBUG', 'true');
      },
      disableDebug: () => {
        localStorage.removeItem('REALTIME_DEBUG');
      },
      threadId,
      instanceId: instanceId.current,
      isDebugEnabled: DEBUG_MODE
    }
  };
}

// Helper to type check ThreadComment
function isThreadComment(data: unknown): data is ThreadComment {
  return (
    typeof data === 'object' && 
    data !== null && 
    'id' in data &&
    'threadId' in data &&
    'content' in data &&
    'createdAt' in data &&
    'authorId' in data
  );
} 
